import { Show, type JSX } from "solid-js";

interface SearchBarProps {
  query: () => string;
  caseSensitive: () => boolean;
  regex: () => boolean;
  wholeWord: () => boolean;
  currentIndex: () => number;
  total: () => number;
  error: () => string | null;
  onQueryChange: (s: string) => void;
  onToggleCase: () => void;
  onToggleRegex: () => void;
  onToggleWholeWord: () => void;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
  onInputRef?: (el: HTMLInputElement) => void;
}

export function SearchBar(p: SearchBarProps): JSX.Element {
  let composing = false;

  const hasQuery = () => p.query().length > 0;
  const hasError = () => p.error() !== null;
  const noMatches = () => hasQuery() && (p.total() === 0 || hasError());

  const onInput = (e: InputEvent & { currentTarget: HTMLInputElement }) => {
    if (composing) return;
    p.onQueryChange(e.currentTarget.value);
  };

  const onCompositionEnd = (e: CompositionEvent & { currentTarget: HTMLInputElement }) => {
    composing = false;
    p.onQueryChange(e.currentTarget.value);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (e.shiftKey) p.onPrev();
      else p.onNext();
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      p.onClose();
    }
  };

  return (
    <form
      class="fd-search-bar"
      role="search"
      aria-label="diff search"
      onSubmit={(e) => e.preventDefault()}
    >
      <input
        ref={(el) => { p.onInputRef?.(el); }}
        type="text"
        class={`fd-search-input${noMatches() ? " fd-search-input-empty" : ""}`}
        aria-label="search query"
        placeholder="検索…"
        value={p.query()}
        title={p.error() ?? undefined}
        onInput={onInput}
        onCompositionStart={() => { composing = true; }}
        onCompositionEnd={onCompositionEnd}
        onKeyDown={onKeyDown}
      />
      <button
        type="button"
        class="fd-search-toggle"
        aria-label="toggle case sensitive"
        aria-pressed={p.caseSensitive()}
        title="大文字小文字を区別"
        onClick={p.onToggleCase}
      >Aa</button>
      <button
        type="button"
        class="fd-search-toggle"
        aria-label="toggle regex"
        aria-pressed={p.regex()}
        title="正規表現"
        onClick={p.onToggleRegex}
      >.*</button>
      <button
        type="button"
        class="fd-search-toggle"
        aria-label="toggle whole word"
        aria-pressed={p.wholeWord()}
        title="単語単位で一致"
        onClick={p.onToggleWholeWord}
      >W</button>
      <Show when={hasQuery()}>
        <span
          class="fd-counter"
          aria-live="polite"
          aria-atomic="true"
        >{p.total() === 0 ? 0 : p.currentIndex() + 1} / {p.total()}</span>
      </Show>
      <button
        type="button"
        class="fd-btn"
        aria-label="previous match"
        disabled={p.total() === 0 || hasError()}
        onClick={p.onPrev}
      >↑</button>
      <button
        type="button"
        class="fd-btn"
        aria-label="next match"
        disabled={p.total() === 0 || hasError()}
        onClick={p.onNext}
      >↓</button>
      <button
        type="button"
        class="fd-btn"
        aria-label="close search"
        onClick={p.onClose}
      >×</button>
    </form>
  );
}
